import { useCallback, useRef, useState, type PointerEvent } from 'react';

export type SwipeDirection = 'left' | 'right';

interface SwipeTabsOptions<T extends string> {
  /** Ordered tab keys, left to right. */
  order: readonly T[];
  active: T;
  onChange: (next: T, direction: SwipeDirection) => void;
  /** Minimum horizontal travel (px) before a gesture counts. Default 56. */
  threshold?: number;
}

interface GestureState {
  id: number;
  x: number;
  y: number;
  /** True once we've decided this gesture is a vertical scroll, not a swipe. */
  vetoed: boolean;
}

/**
 * Horizontal swipe between an ordered set of tabs (touch / pen only).
 *
 * Attach the returned handlers to the tab panel. A gesture becomes a tab
 * change when it travels at least `threshold` px horizontally and stays
 * clearly more horizontal than vertical, so normal vertical scrolling in the
 * panel is never hijacked. Gestures that start inside a form control, a
 * horizontally scrollable element, or anything marked `data-no-swipe` are
 * ignored, which keeps sliders, selects and scrollable tables usable.
 *
 * Returns the direction of the last swipe so the caller can animate the
 * incoming panel from the matching side.
 */
export function useSwipeTabs<T extends string>({
  order,
  active,
  onChange,
  threshold = 56,
}: SwipeTabsOptions<T>) {
  const gesture = useRef<GestureState | null>(null);
  const [lastDirection, setLastDirection] = useState<SwipeDirection | null>(null);

  const onPointerDown = useCallback((e: PointerEvent) => {
    if (e.pointerType === 'mouse') return;
    if (shouldIgnoreTarget(e.target as Element | null)) return;
    gesture.current = { id: e.pointerId, x: e.clientX, y: e.clientY, vetoed: false };
  }, []);

  const onPointerMove = useCallback((e: PointerEvent) => {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId || g.vetoed) return;
    const dx = Math.abs(e.clientX - g.x);
    const dy = Math.abs(e.clientY - g.y);
    // Once the finger has clearly moved vertically, hand the gesture to the
    // scroller for good; a late horizontal flick shouldn't change tabs.
    if (dy > 12 && dy > dx) g.vetoed = true;
  }, []);

  const finish = useCallback(
    (e: PointerEvent) => {
      const g = gesture.current;
      gesture.current = null;
      if (!g || g.id !== e.pointerId || g.vetoed) return;
      const dx = e.clientX - g.x;
      const dy = e.clientY - g.y;
      if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy) * 1.5) return;

      const idx = order.indexOf(active);
      if (idx < 0) return;
      // Finger moving left reveals the tab to the right, and vice versa.
      const direction: SwipeDirection = dx < 0 ? 'left' : 'right';
      const nextIdx = direction === 'left' ? idx + 1 : idx - 1;
      if (nextIdx < 0 || nextIdx >= order.length) return;
      setLastDirection(direction);
      onChange(order[nextIdx], direction);
    },
    [order, active, onChange, threshold],
  );

  const onPointerCancel = useCallback(() => {
    gesture.current = null;
  }, []);

  return {
    lastDirection,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finish,
      onPointerCancel,
    },
  };
}

function shouldIgnoreTarget(el: Element | null): boolean {
  let node: Element | null = el;
  while (node) {
    if (node.hasAttribute('data-no-swipe')) return true;
    const tag = node.tagName;
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return true;
    if (node instanceof HTMLElement && node.scrollWidth > node.clientWidth + 1) {
      const overflowX = getComputedStyle(node).overflowX;
      if (overflowX === 'auto' || overflowX === 'scroll') return true;
    }
    node = node.parentElement;
  }
  return false;
}
